/**
 * DropZone Component
 *
 * A wrapper component that provides drag-and-drop image upload functionality
 * for the Solomon Panel. Shows an overlay when dragging over the panel.
 */

import { useState, useCallback, useRef } from 'react';
import type { ReactNode, DragEvent } from 'react';

import { cn } from '@pray/shared/utils/styles';

import styles from './DropZone.module.scss';

// ============================================================================
// Types
// ============================================================================

export interface DropZoneProps {
  /** Child content to wrap */
  children: ReactNode;
  /** Called when a valid image file is dropped */
  onImageDrop: (file: File) => void;
  /** Whether dropping is disabled */
  disabled?: boolean;
  /** Additional class name */
  className?: string;
}

/** Accepted image MIME types */
const ACCEPTED_TYPES = new Set(['image/jpeg', 'image/jpg', 'image/png', 'image/webp']);

// ============================================================================
// Component
// ============================================================================

export function DropZone({ children, onImageDrop, disabled = false, className }: DropZoneProps) {
  const [isDragActive, setIsDragActive] = useState(false);
  const dragCounterRef = useRef(0);

  /**
   * Check if the dragged items contain an image
   */
  const hasImageFile = useCallback((event: DragEvent): boolean => {
    if (!event.dataTransfer) return false;

    // Check items (modern browsers)
    if (event.dataTransfer.items) {
      const items = Array.from(event.dataTransfer.items);
      const hasImage = items.some((item) => item.kind === 'file' && ACCEPTED_TYPES.has(item.type));
      if (hasImage) return true;
    }

    // Fallback: check types
    if (event.dataTransfer.types.includes('Files')) {
      return true;
    }

    return false;
  }, []);

  /**
   * Handle drag enter - increment counter and activate if first entry
   */
  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      dragCounterRef.current += 1;

      if (hasImageFile(event) && dragCounterRef.current === 1) {
        setIsDragActive(true);
      }
    },
    [disabled, hasImageFile]
  );

  /**
   * Handle drag leave - decrement counter and deactivate if last exit
   */
  const handleDragLeave = useCallback((event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();

    dragCounterRef.current -= 1;

    if (dragCounterRef.current === 0) {
      setIsDragActive(false);
    }
  }, []);

  /**
   * Handle drag over - required to enable drop
   */
  const handleDragOver = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (disabled) return;

      // Set drop effect
      if (event.dataTransfer) {
        event.dataTransfer.dropEffect = 'copy';
      }
    },
    [disabled]
  );

  /**
   * Handle drop - extract image file and call callback
   */
  const handleDrop = useCallback(
    (event: DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      // Reset state
      dragCounterRef.current = 0;
      setIsDragActive(false);

      if (disabled) return;

      const files = event.dataTransfer?.files;
      if (!files || files.length === 0) return;

      // Find the first valid image file
      const validFile = Array.from(files).find((file) => ACCEPTED_TYPES.has(file.type));
      if (validFile) {
        onImageDrop(validFile);
      }
    },
    [disabled, onImageDrop]
  );

  return (
    <div
      className={cn(styles.dropZone, isDragActive && styles.active, disabled && styles.disabled, className)}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {children}

      {/* Drop overlay */}
      {isDragActive && (
        <div className={styles.overlay}>
          <div className={styles.overlayContent}>
            <div className={styles.iconContainer}>
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
                <path
                  d="M21 15l-5-5L5 21"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <span className={styles.overlayText}>Drop image here</span>
            <span className={styles.overlaySubtext}>JPG, PNG, or WebP</span>
          </div>
        </div>
      )}
    </div>
  );
}

export default DropZone;
